'use client';

import { ColorType, createChart, createImageWatermark, IChartApi, LineData, LineSeries, LineSeriesPartialOptions, TickMarkType } from 'lightweight-charts';
import filter from 'lodash/filter';
import find from 'lodash/find';
import forEach from 'lodash/forEach';
import includes from 'lodash/includes';
import isArray from 'lodash/isArray';
import isNumber from 'lodash/isNumber';
import isObject from 'lodash/isObject';
import keys from 'lodash/keys';
import map from 'lodash/map';
import maxBy from 'lodash/maxBy';
import minBy from 'lodash/minBy';
import padStart from 'lodash/padStart';
import sortBy from 'lodash/sortBy';
import * as React from 'react';
import { useEffect, useId, useMemo, useRef, useState } from 'react';

import { ChipLegend, ChipLegendItem } from './chip-legend';

// ---- TypeScript interfaces ----
interface TradeDataItem {
  date?: string;
  forwarded_date?: string;
  original_date?: string | null;
  label?: string;
  value: number;
  year?: number;
  percentile?: number | null;
  formattedDate?: string;
}

interface TradeData {
  watchlist_id?: number;
  group_id?: number | null;
  index?: number;
  short_name?: string | null;
  formula?: string;
  unit_override?: string | null;
  id?: number;
  data: TradeDataItem[];
}

interface SeasonalityLWProps {
  tradeData?: TradeData | null;
  loading?: boolean;
}

const tradeDataRange = (tradeData: TradeData) => {
  if (!tradeData || !isArray(tradeData.data)) {
    return {
      min: 0,
      max: 0,
      maxWithLatestData: 0,
    };
  }

  // Find the latest year
  const latestYear = maxBy(tradeData.data, 'year')?.year;

  // Filter data to only include items from the latest year
  const latestYearData = filter(tradeData.data, { year: latestYear });

  // Find the maximum forwarded_date from the latest year's data
  const maxWithLatestData = latestYearData.length > 0
    ? maxBy(latestYearData, 'forwarded_date')?.forwarded_date
    : 0;

  return {
    min: minBy(tradeData.data, 'forwarded_date')?.forwarded_date,
    max: maxBy(tradeData.data, 'forwarded_date')?.forwarded_date,
    maxWithLatestData,
  };
};

// ---- Data processing helpers ----
function processTradeData (tradeData: TradeData | null): {
  allSeasons: Record<number, LineData[]>;
  fiveYearStats: { avg: LineData[]; min: LineData[]; max: LineData[] };
  availableYears: number[];
} {
  if (!tradeData || !isArray(tradeData.data)) {
    return {
      allSeasons: {},
      fiveYearStats: { avg: [], min: [], max: [] },
      availableYears: [],
    };
  }

  // Group data by year
  const yearData: Record<number, LineData[]> = {};
  const avgData: LineData[] = [];
  const minData: LineData[] = [];
  const maxData: LineData[] = [];

  forEach(tradeData.data, (item) => {
    if (!item.forwarded_date || !isNumber(item.value) || !isNumber(item.year)) return;

    const date = new Date(item.forwarded_date);
    if (isNaN(date.getTime())) return;

    const lineData: LineData = { time: {year: date.getUTCFullYear(), month: date.getUTCMonth() + 1, day: date.getUTCDate() }, value: item.value };

    if (item.year === -5) {
      // 5-year average
      avgData.push(lineData);
    } else if (item.year === 1) {
      // 5-year max
      maxData.push(lineData);
    } else if (item.year === 0) {
      // 5-year min
      minData.push(lineData);
    } else if (item.year > 1) {
      // Regular year data
      if (!yearData[item.year]) {
        yearData[item.year] = [];
      }
      yearData[item.year].push(lineData);
    }
  });

  // Helper function to sort and deduplicate data by time
  const sortAndDeduplicateData = (data: LineData[], yearLabel?: string): LineData[] => {
    // Sort by time first
    const sorted = sortBy(data, (item) => {
      const time = item.time as { year: number; month: number; day: number };
      return new Date(time.year, time.month - 1, time.day).getTime();
    });

    // Remove duplicates by keeping only the last occurrence of each time
    const deduplicated: LineData[] = [];
    const seenTimes = new Set<string>();
    let duplicateCount = 0;

    // Process in reverse order to keep the last occurrence
    for (let i = sorted.length - 1; i >= 0; i--) {
      const item = sorted[i];
      const time = item.time as { year: number; month: number; day: number };
      const timeKey = `${time.year}-${time.month}-${time.day}`;

      if (!seenTimes.has(timeKey)) {
        seenTimes.add(timeKey);
        deduplicated.unshift(item); // Add to beginning to maintain order
      } else {
        // Found a duplicate - this should not happen
        duplicateCount++;
        console.warn(
          '[Seasonality] Duplicate data point found for ' + (yearLabel || 'unknown year') + ': ' +
          'date=' + timeKey + ', value=' + item.value + '. This indicates a backend data issue.'
        );
      }
    }

    if (duplicateCount > 0) {
      console.warn(
        '[Seasonality] Removed ' + duplicateCount + ' duplicate(s) for ' + (yearLabel || 'unknown year') + '. ' +
        'Backend should not return duplicates for the same (year, forwarded_date) combination.'
      );
    }

    return deduplicated;
  };

  // Sort and deduplicate data for each year
  forEach(yearData, (data, year) => {
    yearData[year] = sortAndDeduplicateData(data, `year ${year}`);
  });

  // Sort and deduplicate statistical data
  const fiveYearStats = {
    avg: sortAndDeduplicateData(avgData, '5-year average'),
    min: sortAndDeduplicateData(minData, '5-year min'),
    max: sortAndDeduplicateData(maxData, '5-year max'),
  };

  const availableYears = sortBy(map(keys(yearData), Number), (year) => -year); // descending order

  return {
    allSeasons: yearData,
    fiveYearStats,
    availableYears,
  };
}

// HSL → hex. Used to generate the muted tail below with a lightness cap, so
// no year ever fades toward the white chart background (mirrors the fix in
// days-to-expiry-chart.tsx). Standard CSS-Color-4 a/f conversion trick:
// f(0), f(8), f(4) give R, G, B respectively.
function hsl (h: number, s: number, l: number): string {
  const a = s * Math.min(l, 1 - l);
  const f = (n: number): string => {
    const k = (n + h / 30) % 12;
    const c = l - a * Math.max(-1, Math.min(k - 3, 9 - k, 1));
    return padStart(Math.round(255 * c).toString(16), 2, '0');
  };
  return `#${f(0)}${f(8)}${f(4)}`;
}

// Same 8 hues the old tab20-derived palette used for its non-pastel entries
// (blue/orange/purple/teal/pink/brown/neutral/cyan) — yellow, lime, and every
// pastel are dropped for lacking contrast on the white chart background. Each
// "lap" through the 8 hues steps lightness up (older = lighter), capped at
// 0.72 so even a many-years-back season stays visibly a color, not cream.
const TAIL_HUES = [207, 33, 261, 174, 316, 19, 0, 187];
function tailColor (i: number): string {
  const hue = TAIL_HUES[i % TAIL_HUES.length];
  const lap = Math.floor(i / TAIL_HUES.length);
  const sat = hue === 0 ? 0.08 : 0.35; // keep the "neutral" slot actually neutral
  const lightness = Math.min(0.72, 0.42 + lap * 0.14);
  return hsl(hue, sat, lightness);
}

// Color palette indexed by recency (index 0 = latest year, which is drawn in
// LATEST_YEAR_COLOR instead, so slot 0 is only reached if the palette cycles
// — kept as a documented placeholder, never actually rendered). The first
// three slots pin the house seasonality convention shared with the PowerBI
// dashboards, FundamentalsSeasonalityChart, and the MCP seasonality chart
// (apps/mcp-server/ui/src/seasonality.ts, kept in sync by hand): latest year
// black, previous year red, the year before green. Older years step through
// TAIL_HUES, getting lighter with age — 32 entries (4 laps) is far beyond any
// realistic lookback, so the array never wraps back around to black.
const YEAR_COLORS = [
  '#000000',
  '#D32F2F',
  '#2E7D32',
  ...Array.from({ length: 32 }, (_, i) => tailColor(i)),
];

// Latest year styling
const LATEST_YEAR_COLOR = '#000000'; // black
const LATEST_YEAR_LINE_WIDTH = 2; // thicker than default
const DEFAULT_LINE_WIDTH = 1;

// 5-year stat bands read as context, not competing series: a mid-grey dashed
// average with light-grey min/max (distinguished from each other by dash
// pattern, not color, since min sits below the years and max above). Grey
// also sidesteps any collision with a year's color — the old '#ff7300' max
// happened to match the year palette's orange.
const STAT_AVG_COLOR = '#6b7280';
const STAT_BAND_COLOR = '#b0b6c0';

// Custom tooltip formatter
function formatTooltipDate (time: { year: number; month: number; day: number }): string {
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${monthNames[time.month - 1]} ${time.day}`;
}

const buildForwardedDateKey = (time: { year: number; month: number; day: number }): string => {
  return `${time.year}-${padStart(String(time.month), 2, '0')}-${padStart(String(time.day), 2, '0')}`;
};

export default function SeasonalityLW ({ tradeData, loading = false }: SeasonalityLWProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const tooltipId = useId();

  // Process trade data
  const { allSeasons, fiveYearStats, availableYears } = useMemo(() =>
    processTradeData(tradeData || null), [tradeData]);

  // Get the range of the trade data
  const { max: maxForwardedDate, maxWithLatestData } = useMemo(() => tradeDataRange(tradeData || { data: [] }), [tradeData]);

  const tradeDataLookup = useMemo(() => {
    if (!tradeData || !isArray(tradeData.data)) {
      return {} as Record<number, Record<string, TradeDataItem>>;
    }

    const lookup: Record<number, Record<string, TradeDataItem>> = {};
    forEach(tradeData.data, (item) => {
      if (!isNumber(item.year) || !item.forwarded_date) return;
      if (!lookup[item.year]) {
        lookup[item.year] = {};
      }
      lookup[item.year][item.forwarded_date] = item;
    });

    return lookup;
  }, [tradeData]);

  // Initialize visible years with the latest 3 available years
  const [visibleYears, setVisibleYears] = useState<number[]>([]);
  const [visibleStats, setVisibleStats] = useState<{ avg: boolean; min: boolean; max: boolean }>({
    avg: false,
    min: false,
    max: false,
  });

  // Update visible years when availableYears changes
  useEffect(() => {
    if (availableYears.length > 0) {
      const latestThreeYears = availableYears.slice(0, 3);
      setVisibleYears(latestThreeYears);
    }
  }, [availableYears]);

  const toggleYearVisibility = (year: number) => {
    setVisibleYears(prev =>
      includes(prev, year)
        ? filter(prev, y => y !== year)
        : [...prev, year].sort((a, b) => b - a) // Sort descending
    );
  };

  const toggleStatVisibility = (stat: 'avg' | 'min' | 'max') => {
    setVisibleStats(prev => ({
      ...prev,
      [stat]: !prev[stat],
    }));
  };

  useEffect(() => {
    if (!containerRef.current) return;

    const chart = createChart(containerRef.current, {
      autoSize: true,
      rightPriceScale: {
        visible: true,
        borderVisible: true,
      },
      leftPriceScale: { visible: false },
      layout: {
        background: { type: ColorType.Solid, color: 'white' },
        textColor: '#222',
      },
      grid: {
        vertLines: { visible: true, color: '#e6e6e6' },
        horzLines: { visible: true, color: '#e6e6e6' },
      },
      crosshair: { mode: 0, vertLine: { labelVisible: false } },
      timeScale: {
        // Standard dates will show like "Oct 2024, Jan 2025 …", similar to ACL
        rightOffset: 0,
        fixLeftEdge: true,
        fixRightEdge: true,
        allowBoldLabels: false,
        tickMarkFormatter: (time: { year: number; month: number; day: number }, tickMarkType: TickMarkType) => {
          const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
          if (time && isObject(time) && time.month && (tickMarkType === TickMarkType.Month || tickMarkType === TickMarkType.Year)) {
            const month = monthNames[time.month - 1] || '';

            // If tick is more than 1 year from the max forwarded date, return the year as (Y - <years-back>)
            if (maxForwardedDate && new Date(time.year, time.month - 1, time.day) <= new Date(new Date(maxForwardedDate).getUTCFullYear() - 1, new Date(maxForwardedDate).getUTCMonth(), new Date(maxForwardedDate).getUTCDate())) {
              return `${month} (Y-${new Date(maxForwardedDate).getUTCFullYear() - time.year})`;
            }

            return monthNames[time.month - 1] || '';
          }

          return time.day;
        },
      },
    });

    chartRef.current = chart;

    // Add watermark
    const img = new window.Image();
    img.src = '/assets/hilo-watermark.png';
    img.onload = () => {
      // Guard against chart being destroyed before image loads
      const pane = chart.panes()?.[0];
      if (!pane) return;
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(img, 0, 0);
        const dataUrl = canvas.toDataURL('image/png');
        createImageWatermark(pane, dataUrl, {
          alpha: 0.05,
          padding: 20,
        });
      }
    };

    const common: LineSeriesPartialOptions = {
      priceLineVisible: false,
      lastValueVisible: false,
      crosshairMarkerVisible: true,
      lineWidth: 2,
    };

    // Add line series for 5-year average (dashed, mid-grey — reads as context)
    const avgSeries = chart.addSeries(LineSeries, {
      ...common,
      lineWidth: 1,
      color: STAT_AVG_COLOR,
      lineStyle: 2, // LineStyle.Dashed
    });

    // Add line series for 5-year min (dotted, light-grey — same color as max,
    // distinguished by dash pattern since they bound the range from opposite sides)
    const minSeries = chart.addSeries(LineSeries, {
      ...common,
      lineWidth: 1,
      color: STAT_BAND_COLOR,
      lineStyle: 1, // LineStyle.Dotted
    });

    // Add line series for 5-year max (large-dashed, light-grey)
    const maxSeries = chart.addSeries(LineSeries, {
      ...common,
      lineWidth: 1,
      color: STAT_BAND_COLOR,
      lineStyle: 3, // LineStyle.LargeDashed
    });

    // Add series for each year
    const seriesMap: Record<number, any> = {};
    forEach(availableYears, (year, index) => {
      const isLatestYear = index === 0; // First year in the sorted list is the latest
      const series = chart.addSeries(LineSeries, {
        ...common,
        lineWidth: isLatestYear ? LATEST_YEAR_LINE_WIDTH : DEFAULT_LINE_WIDTH,
        color: isLatestYear ? LATEST_YEAR_COLOR : YEAR_COLORS[index % YEAR_COLORS.length],
      });
      seriesMap[year] = series;
    });

    // Set data for 5-year statistics (only if visible)
    if (visibleStats.avg) {
      avgSeries.setData(fiveYearStats.avg);
    } else {
      avgSeries.setData([]);
    }

    if (visibleStats.min) {
      minSeries.setData(fiveYearStats.min);
    } else {
      minSeries.setData([]);
    }

    if (visibleStats.max) {
      maxSeries.setData(fiveYearStats.max);
    } else {
      maxSeries.setData([]);
    }

    // Set data for visible years
    forEach(visibleYears, year => {
      if (seriesMap[year] && allSeasons[year]) {
        seriesMap[year].setData(allSeasons[year]);
      }
    });

    // Crosshair move handler used for subscribe/unsubscribe
    const handleCrosshairMove = (param: any) => {
      if (param.point === undefined || !param.time || param.point.x < 0 || param.point.y < 0) {
        return;
      }

      const time = param.time as { year: number; month: number; day: number };
      const dateStr = formatTooltipDate(time);

      // Create tooltip content
      let tooltipContent = '<div style="background: white; border: 1px solid #ccc; border-radius: 4px; padding: 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.1); font-family: Arial, sans-serif; font-size: 12px;">';
      tooltipContent += `<div style="font-weight: bold; margin-bottom: 4px;">${dateStr}</div>`;

      // Add 5-year statistics (only if visible)
      if (visibleStats.avg && param.seriesData.get(avgSeries)) {
        const data = param.seriesData.get(avgSeries) as LineData;
        if (data && isNumber(data.value)) {
          tooltipContent += '<div style="display: flex; align-items: center; margin: 2px 0;">';
          tooltipContent += `<span style="display: inline-block; width: 8px; height: 2px; background-color: ${STAT_AVG_COLOR}; margin-right: 6px; border-radius: 1px;"></span>`;
          tooltipContent += `<span>Avg (5Yr): <strong>${data.value.toFixed(2)}</strong></span>`;
          tooltipContent += '</div>';
        }
      }

      // Add Min (5Yr) (only if visible)
      if (visibleStats.min && param.seriesData.get(minSeries)) {
        const data = param.seriesData.get(minSeries) as LineData;
        if (data && isNumber(data.value)) {
          tooltipContent += '<div style="display: flex; align-items: center; margin: 2px 0;">';
          tooltipContent += `<span style="display: inline-block; width: 8px; height: 2px; background-color: ${STAT_BAND_COLOR}; margin-right: 6px; border-radius: 1px;"></span>`;
          tooltipContent += `<span>Min (5Yr): <strong>${data.value.toFixed(2)}</strong></span>`;
          tooltipContent += '</div>';
        }
      }

      // Add Max (5Yr) (only if visible)
      if (visibleStats.max && param.seriesData.get(maxSeries)) {
        const data = param.seriesData.get(maxSeries) as LineData;
        if (data && isNumber(data.value)) {
          tooltipContent += '<div style="display: flex; align-items: center; margin: 2px 0;">';
          tooltipContent += `<span style="display: inline-block; width: 8px; height: 2px; background-color: ${STAT_BAND_COLOR}; margin-right: 6px; border-radius: 1px;"></span>`;
          tooltipContent += `<span>Max (5Yr): <strong>${data.value.toFixed(2)}</strong></span>`;
          tooltipContent += '</div>';
        }
      }

      // Add values for each visible series
      forEach(visibleYears, year => {
        const series = seriesMap[year];
        if (series && param.seriesData.get(series)) {
          const data = param.seriesData.get(series) as LineData;
          if (data && isNumber(data.value)) {
            const colorIndex = availableYears.indexOf(year);
            const isLatestYear = colorIndex === 0;
            const color = isLatestYear ? LATEST_YEAR_COLOR : YEAR_COLORS[colorIndex % YEAR_COLORS.length];

            const timeValue = data.time as { year: number; month: number; day: number };
            const forwardedDateKey = timeValue && timeValue.year && timeValue.month && timeValue.day
              ? buildForwardedDateKey(timeValue)
              : undefined;
            const tradeDataItem = forwardedDateKey ? tradeDataLookup[year]?.[forwardedDateKey] : undefined;
            const labelSuffix = tradeDataItem?.label ? ` (${tradeDataItem.label})` : '';
            const displayLabel = `${year.toString()}${labelSuffix}`;

            tooltipContent += '<div style="display: flex; align-items: center; margin: 2px 0;">';
            tooltipContent += `<span style="display: inline-block; width: 8px; height: 8px; background-color: ${color}; border-radius: 50%; margin-right: 6px;"></span>`;
            tooltipContent += `<span>${displayLabel}: <strong>${data.value.toFixed(2)}</strong></span>`;
            tooltipContent += '</div>';
          }
        }
      });

      tooltipContent += '</div>';

      // Update tooltip position and content
      const tooltip = document.getElementById(tooltipId);
      if (tooltip && containerRef.current) {
        tooltip.innerHTML = tooltipContent;
        tooltip.style.display = 'block';

        // Get container width to determine if cursor is in right portion
        const containerWidth = containerRef.current.clientWidth;
        const cursorX = param.point.x;
        const isInRightHalf = cursorX > containerWidth * 0.5;

        // Position tooltip to the left if cursor is in right half of chart
        if (isInRightHalf) {
          // Force a reflow to get accurate tooltip width
          void tooltip.offsetWidth;
          const tooltipWidth = tooltip.getBoundingClientRect().width || 200;
          const leftPosition = cursorX - tooltipWidth - 10;
          // Ensure tooltip doesn't go off the left edge
          tooltip.style.left = `${Math.max(10, leftPosition)}px`;
        } else {
          tooltip.style.left = `${cursorX + 10}px`;
        }

        tooltip.style.top = `${param.point.y - 10}px`;
      }
    };

    // Subscribe to crosshair move to show custom tooltip
    chart.subscribeCrosshairMove(handleCrosshairMove);

    // Unsubscribe crosshair when cursor leaves chart, resubscribe on enter
    const containerEl = containerRef.current;
    const handleMouseLeave = () => {
      try {
        chart.unsubscribeCrosshairMove(handleCrosshairMove);
      } catch (error) {
        console.warn('Seasonality chart: unable to unsubscribe crosshair move on mouse leave', error);
      }
      const tooltip = document.getElementById(tooltipId);
      if (tooltip) {
        tooltip.style.display = 'none';
      }
    };

    const handleMouseEnter = () => {
      chart.subscribeCrosshairMove(handleCrosshairMove);
    };

    containerEl?.addEventListener('mouseleave', handleMouseLeave);
    containerEl?.addEventListener('mouseenter', handleMouseEnter);

    // Only set visible range if we have some visible data
    const hasVisibleData = visibleYears.length > 0 || visibleStats.avg || visibleStats.min || visibleStats.max;

    // Set visible range using maxWithLatestData logic
    if (hasVisibleData && maxForwardedDate && maxWithLatestData && !isNaN(new Date(maxForwardedDate).getTime()) && !isNaN(new Date(maxWithLatestData).getTime())) {
      const maxDate = new Date(maxForwardedDate);
      const maxWithLatestDate = new Date(maxWithLatestData);

      // Calculate the years between maxForwardedDate and maxWithLatestData
      let yearsDiff = maxDate.getUTCFullYear() - maxWithLatestDate.getUTCFullYear();

      // If maxForwardedDate < maxWithLatestDate - yearsDiff, add 1 to yearsDiff
      if (maxWithLatestDate < new Date(maxDate.getUTCFullYear() - yearsDiff, maxDate.getUTCMonth(), maxDate.getUTCDate())) {
        yearsDiff += 1;
      }

      // Set the bottom of the visible range
      // Take the Day and Month from maxForwardedDate, set year to maxForwardedDate.year - yearDiff
      const bottomDate = new Date(maxDate);
      bottomDate.setUTCFullYear(maxDate.getUTCFullYear() - yearsDiff);

      // Validate that bottomDate is valid and before maxDate
      if (!isNaN(bottomDate.getTime()) && bottomDate < maxDate) {
        const visibleRange = {
          from: bottomDate.getTime() / 1000 as any,
          to: maxDate.getTime() / 1000 as any,
        };

        chart.timeScale().setVisibleRange(visibleRange);
      } else {
        // Fall back to logical range if date calculation is invalid
        console.warn('Invalid date range calculation, using logical range');
        chart.timeScale().setVisibleLogicalRange({ from: 0, to: 100 });
      }
    } else if (hasVisibleData && maxForwardedDate && !isNaN(new Date(maxForwardedDate).getTime())) {
      // Fallback to 1 year before maxForwardedDate if maxWithLatestData is not available
      const maxDate = new Date(maxForwardedDate);
      const oneYearBefore = new Date(maxDate);
      oneYearBefore.setUTCFullYear(maxDate.getUTCFullYear() - 1);

      // Validate that oneYearBefore is valid and before maxDate
      if (!isNaN(oneYearBefore.getTime()) && oneYearBefore < maxDate) {
        const fallbackRange = {
          from: oneYearBefore.getTime() / 1000 as any,
          to: maxDate.getTime() / 1000 as any,
        };
        chart.timeScale().setVisibleRange(fallbackRange);
      } else {
        // Fall back to logical range if date calculation is invalid
        console.warn('Invalid fallback date range calculation, using logical range');
        chart.timeScale().setVisibleLogicalRange({ from: 0, to: 100 });
      }
    } else if (hasVisibleData) {
      // Fallback to logical range if no maxForwardedDate but we have visible data
      chart.timeScale().setVisibleLogicalRange({ from: 0, to: 100 });
    }

    // Do not attach our own ResizeObserver when using autoSize: true.
    // lightweight-charts already manages resizing internally, and a second
    // observer that mutates options can trigger ResizeObserver loops.

    return () => {
      // No custom ResizeObserver to clean up
      // Remove mouse listeners and unsubscribe crosshair
      containerEl?.removeEventListener('mouseleave', handleMouseLeave);
      containerEl?.removeEventListener('mouseenter', handleMouseEnter);
      try {
        chart.unsubscribeCrosshairMove(handleCrosshairMove);
      } catch (error) {
        console.warn('Seasonality chart: unable to unsubscribe crosshair move during cleanup', error);
      }
      chart.remove();
    };
  }, [allSeasons, visibleYears, fiveYearStats, visibleStats, availableYears, maxForwardedDate, maxWithLatestData, tradeDataLookup, tooltipId]);

  // Show loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-full w-full text-gray-400">
        Loading...
      </div>
    );
  }

  // Show empty state if no data
  if (!tradeData || !isArray(tradeData.data) || tradeData.data.length === 0) {
    return (
      <div className="flex items-center justify-center h-full w-full text-gray-400">
        No data available
      </div>
    );
  }

  const yearLegendItems: ChipLegendItem[] = map(availableYears, (year, index) => {
    const isLatestYear = index === 0;
    const yearColor = isLatestYear ? LATEST_YEAR_COLOR : YEAR_COLORS[index % YEAR_COLORS.length];
    const representativeItem = find(tradeData?.data, (item) => item.year === year && !!item.label);
    return {
      key: String(year),
      label: year.toString(),
      color: yearColor,
      hidden: !includes(visibleYears, year),
      emphasized: isLatestYear,
      tooltip: representativeItem?.label || undefined,
    };
  });

  const statLegendItems: ChipLegendItem[] = [
    { key: 'avg', label: 'Avg (5Yr)', color: STAT_AVG_COLOR, hidden: !visibleStats.avg, swatch: 'dash' },
    { key: 'min', label: 'Min (5Yr)', color: STAT_BAND_COLOR, hidden: !visibleStats.min, swatch: 'dash' },
    { key: 'max', label: 'Max (5Yr)', color: STAT_BAND_COLOR, hidden: !visibleStats.max, swatch: 'dash' },
  ];

  return (
    <div className="h-full w-full min-h-0 flex flex-col">
      {/* Legend */}
      <div className="py-2 px-4 border-b min-h-14 flex justify-center">
        <ChipLegend
          items={[...yearLegendItems, ...statLegendItems]}
          onToggle={(key) => {
            if (key === 'avg' || key === 'min' || key === 'max') {
              toggleStatVisibility(key);
            } else {
              toggleYearVisibility(Number(key));
            }
          }}
        />
      </div>

      {/* Chart */}
      <div className="flex-1 min-h-0 min-w-0 relative overflow-hidden" ref={containerRef}>
        {/* Custom tooltip */}
        <div
          id={tooltipId}
          style={{
            position: 'absolute',
            display: 'none',
            zIndex: 1000,
            pointerEvents: 'none',
          }}
        />
      </div>
    </div>
  );
}
